import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { 
  HANDLE_API_ERROR,
  NETWORK_ERROR,
  AUTH_ERROR,
  CLEAR_ERROR,
  SHOW_ERROR_MESSAGE,
  HIDE_ERROR_MESSAGE,
  networkError,
  authError,
  clearError,
  showErrorMessage
} from './actions';

// Error type constants
const AUTH_ERROR_CODES = [401, 403];
const NETWORK_ERROR_CODES = [500, 502, 503, 504];
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

/**
 * Determines if an error is an authentication error
 * @param {Object} error - Error object
 * @returns {boolean} - True if auth error
 */
function isAuthError(error) {
  if (error.response) {
    return AUTH_ERROR_CODES.includes(error.response.status);
  }
  return false;
}

/**
 * Determines if an error is a network error
 * @param {Object} error - Error object
 * @returns {boolean} - True if network error
 */
function isNetworkError(error) {
  // No response means network error
  if (!error.response) {
    return true;
  }

  // Check for network error status codes
  if (error.response.status && NETWORK_ERROR_CODES.includes(error.response.status)) {
    return true;
  }

  // Check for timeout codes
  if (error.code && TIMEOUT_CODES.includes(error.code)) {
    return true;
  }

  return false;
}

/**
 * Gets appropriate error message based on error type
 * @param {Object} error - Error object
 * @param {string} context - Error context
 * @returns {string} - Error message
 */
function getErrorMessage(error, context) {
  // Auth error messages
  if (isAuthError(error)) {
    if (error.response.status === 401) {
      return 'Sesión expirada. Por favor, inicie sesión nuevamente.';
    }
    if (error.response.status === 403) {
      return 'No tiene permisos para realizar esta acción.';
    }
  }

  // Network error messages
  if (isNetworkError(error)) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'La solicitud tardó demasiado. Por favor, intente nuevamente.';
    }
    
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return 'Error de conexión. Por favor, verifique su conexión a internet.';
    }

    if (error.response?.status >= 500) {
      return 'Error del servidor. Por favor, intente más tarde.';
    }
  }

  // Default message
  return 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.';
}

/**
 * Main API error handler saga
 * @param {Object} action - Redux action with error and context
 */
function* handleApiErrorSaga(action) {
  try {
    const { error, context } = action.payload;

    if (isAuthError(error)) {
      const message = getErrorMessage(error, context);
      yield put(authError(error, context, message));
    } else if (isNetworkError(error)) {
      const message = getErrorMessage(error, context);
      yield put(networkError(error, context, message));
    } else {
      // Unknown error type - treat as network error
      const message = getErrorMessage(error, context);
      yield put(networkError(error, context, message));
    }

  } catch (sagaError) {
    console.error('Error in handleApiErrorSaga:', sagaError);
    // Fallback to network error
    yield put(networkError(sagaError, action.payload.context, 'Error interno del sistema.'));
  }
}

/**
 * Handles network errors (shows message, no logout)
 * @param {Object} action - Redux action with network error
 */
function* handleNetworkErrorSaga(action) {
  try {
    const { message, context } = action.payload;

    // Show error message
    yield put(showErrorMessage(message, 'network', context));

    // Log error for debugging
    console.warn('Network error:', {
      context,
      message,
      error: action.payload.error
    });

  } catch (sagaError) {
    console.error('Error in handleNetworkErrorSaga:', sagaError);
  }
}

/**
 * Handles authentication errors (shows message, triggers logout)
 * @param {Object} action - Redux action with auth error
 */
function* handleAuthErrorSaga(action) {
  try {
    const { message, context } = action.payload;

    // Show error message
    yield put(showErrorMessage(message, 'auth', context));

    // Trigger logout
    yield put({
      type: 'LOGOUT_USER',
      payload: { history: null } // Will be handled by the component
    });

    // Log error for debugging
    console.warn('Auth error - user will be logged out:', {
      context,
      message,
      error: action.payload.error
    });

  } catch (sagaError) {
    console.error('Error in handleAuthErrorSaga:', sagaError);
  }
}

/**
 * Clears error state
 */
function* clearErrorSaga() {
  try {
    yield put({ type: HIDE_ERROR_MESSAGE });
    yield put(clearError());
  } catch (sagaError) {
    console.error('Error in clearErrorSaga:', sagaError);
  }
}

/**
 * Main network error handling saga watcher
 */
export function* networkErrorHandlingSaga() {
  yield takeEvery(HANDLE_API_ERROR, handleApiErrorSaga);
  yield takeEvery(NETWORK_ERROR, handleNetworkErrorSaga);
  yield takeEvery(AUTH_ERROR, handleAuthErrorSaga);
  yield takeEvery('CLEAR_ERROR_REQUEST', clearErrorSaga);
}

// Export individual sagas for testing
export { 
  handleApiErrorSaga, 
  handleNetworkErrorSaga, 
  handleAuthErrorSaga, 
  clearErrorSaga,
  isAuthError,
  isNetworkError,
  getErrorMessage
};
